import Icon from '@/components/snippets/icon/icon'
import { useAccountLayoutContext } from './AccountProfileContext'
import { useMemo } from 'preact/hooks'
import { accountTranslations } from '../helpers/translations'

export const ANCHOR_TAGS = {
  IDENTIFIERS: {
    tag: 'IDENTIFIERS',
    title: accountTranslations.kids_profiles.identifiers.title
  },
  PROFILE_DETAILS: {
    tag: 'PROFILE_DETAILS',
    title: accountTranslations.kids_profiles.profile_details.title
  },
  SHOE_WARDROBE: {
    tag: 'SHOE_WARDROBE',
    title: accountTranslations.kids_profiles.shoe_wardrobe.title
  },
  MEASUREMENTS: {
    tag: 'MEASUREMENTS',
    title: accountTranslations.kids_profiles.measurements.title
  },
  FAVOURITE_BRANDS: {
    tag: 'FAVOURITE_BRANDS',
    title: accountTranslations.kids_profiles.favourite_brands.title
  },
  FAVOURITE_SPORTS: {
    tag: 'FAVOURITE_SPORTS',
    title: accountTranslations.kids_profiles.favourite_sports.title
  },
  WISHLIST: {
    tag: 'WISHLIST',
    title: accountTranslations.kids_profiles.wishlist.title
  }
}

/**
 *
 *
 * @param {{cta: {hook: string, title: string, callback: (e) => void}}} param0
 */
const PageHeaderCTA = ({ cta }) => {
  const isValidCTA = cta?.title && cta?.hook && cta?.callback
  if (!isValidCTA) return <></>
  return (
    <>
      <div data-role={cta.hook} onClick={cta.callback} className={'btn btn-solid-black btn-md items-center gap-2 '}>
        <Icon id={'plus'} />
        <div className={'paragraph-sm'}>{cta.title}</div>
      </div>
    </>
  )
}

/**
 * @param {{title: string, cta: {hook: string, title: string, callback: (e) => void}, ctaChild?: Readonly<React.ReactNode>}} param0
 */
export const PageHeader = ({ title, cta, ctaChild, icon }) => {
  return (
    <div className={'flex flex-row items-center justify-between gap-3'}>
      {!!icon && <>{icon}</>}
      <h1 className={'h1 font-bold'}>{title}</h1>
      {ctaChild ? <>{ctaChild}</> : <PageHeaderCTA cta={cta} />}
    </div>
  )
}

/**
 *
 * @param {{state: string}} param0
 * @returns
 */
export const PageBackButton = ({ state }) => {
  const { handlePushState } = useAccountLayoutContext()
  const handleBack = () => {
    handlePushState(state, true)
  }
  return (
    <div onClick={handleBack} className={'flex flex-row gap-2'}>
      <Icon id="arrow-left" className="my-auto" />
      <div className={'btn btn-text btn-inline underline underline-offset-2'}>Back</div>
    </div>
  )
}

export const PageAnchorTags = () => {
  return (
    <div className={'flex flex-row gap-6 max-w-full overflow-x-auto overflow-y-hidden'}>
      {Object.keys(ANCHOR_TAGS).map((tag) => {
        return (
          <a
            className={'btn btn-text btn-md btn-inline capitalize underline underline-offset-2'}
            href={`#${ANCHOR_TAGS[tag].tag}`}
          >
            <div className={'min-h-12'}>{ANCHOR_TAGS[tag].title}</div>
          </a>
        )
      })}
    </div>
  )
}

const handleSeparatorGap = (type) => {
  switch (type) {
    case 'xs': {
      return 'py-1'
    }
    case 'sm': {
      return 'py-2'
    }
    case 'md': {
      return 'py-4'
    }
    case 'lg': {
      return 'py-6'
    }
    default: {
      return 'py-0'
    }
  }
}

/**
 *
 * @param {{type: 'xs' | 'sm' | 'md' |'lg'}} props
 * @returns
 */
export const Separator = (props) => {
  return (
    <div className={'w-full'}>
      <div className={handleSeparatorGap(props?.type)}>
        <hr />
      </div>
    </div>
  )
}

/**
 *
 * @param {{anchorTag: string, title: string, cta: {title: string, callback:(e) => void, hook: string, display?: boolean, icon: 'plus' }, children: Readonly<React.ReactNode>, showDivider: boolean}} props
 * @returns
 */
export const AccountSectionWrapper = ({
  anchorTag,
  title,
  cta,
  children,
  showDivider = true,
  mobileCtaBottom = false,
  mobileCtaAlign = 'flex-end',
  isPage = false
}) => {
  const displayCTA = useMemo(() => {
    if (cta?.display !== undefined) {
      return cta?.display
    }
    return !!cta
  }, [cta])

  return (
    <div id={anchorTag} className={'flex scroll-mt-24 flex-col gap-6'}>
      <div className={'flex w-full flex-col justify-between gap-2 md:flex-row'}>
        {isPage ? <PageHeader title={title} /> : <div className={'h3 font-bold text-primary-blue'}>{title}</div>}
        {displayCTA && <CTA cta={cta} className={mobileCtaBottom ? 'hidden md:block' : ''} />}
      </div>
      {children}
      {!!displayCTA && !!mobileCtaBottom && (
        <div className={`flex ${mobileCtaAlign}`}>
          <CTA cta={cta} className={'block md:hidden'} />
        </div>
      )}
    </div>
  )
}

const CTA = ({ cta, className }) => {
  return (
    <div
      data-role={cta?.hook}
      onClick={cta?.callback}
      className={
        (className ?? '') +
        ' ' +
        (cta?.textButton
          ? 'btn btn-text btn-md btn-inline underline underline-offset-2'
          : 'flex w-fit cursor-pointer flex-row items-center gap-2 rounded-3xl border border-solid border-black p-2 px-4')
      }
    >
      {cta?.icon && <Icon id={cta?.icon} />}
      <div className={cta?.textButton ? 'paragraph-md font-bold text-primary-black' : 'paragraph-sm'}>{cta?.title}</div>
    </div>
  )
}
